import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import FeaturesBar from "@/components/feature/FeaturesBar";
import CartDrawer from "@/components/feature/CartDrawer";
import Breadcrumb from "@/pages/home/components/Breadcrumb";
import ProductGallery from "@/pages/product/components/ProductGallery";
import { fetchNuvemshopProducts, fetchSuggestedProducts } from "@/services/nuvemshop";
import type { Product } from "@/mocks/products";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/utils/format";
import { defaultVariant, defaultVariantLabel, variantImage, variantLabel, variantMaxQuantity } from "@/utils/variants";
import RelatedProducts from "@/pages/product/components/RelatedProducts";

export default function LiveProduct() {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const [imageIndex, setImageIndex] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<Product[]>([]);
  const { addItem, openCart } = useCart();

  useEffect(() => {
    let active = true;
    setProduct(null);
    setError("");
    setQuantity(1);
    setSelectedValues({});
    setImageIndex(0);
    window.scrollTo({ top: 0, behavior: "auto" });
    fetchNuvemshopProducts({ productId: id })
      .then((items) => {
        if (!active) return;
        const found = items[0] ?? null;
        setProduct(found);
        if (!found) {
          setError("Produto não encontrado.");
          return;
        }
        const firstVariant = found.variants?.find((variant) => variant.inStock) ?? found.variants?.[0];
        if (firstVariant) setSelectedValues(firstVariant.values);
      })
      .catch(() => {
        if (active) setError("Não foi possível carregar este produto. Tente novamente em alguns instantes.");
      });
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    setSuggested([]);
    if (!id) return;
    fetchSuggestedProducts(id)
      .then((items) => { if (active) setSuggested(items); })
      .catch(() => { /* seção opcional: silenciosamente não mostra nada */ });
    return () => { active = false; };
  }, [id]);

  const attributeNames = useMemo(() => Object.keys(product?.attributes ?? {}), [product]);

  const selectedVariant = useMemo(() => {
    if (!product?.variants || product.variants.length === 0) return null;
    return (
      product.variants.find((variant) =>
        attributeNames.every((name) => variant.values[name] === selectedValues[name])
      ) ?? null
    );
  }, [product, attributeNames, selectedValues]);

  // Quando a variante escolhida tem uma foto vinculada na Nuvemshop, troca a
  // imagem principal pra ela.
  useEffect(() => {
    if (!product || !selectedVariant?.imageId || !product.imageIds) return;
    const index = product.imageIds.indexOf(selectedVariant.imageId);
    if (index >= 0) setImageIndex(index);
  }, [product, selectedVariant]);

  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;
  const displayCompareAtPrice = selectedVariant ? selectedVariant.compareAtPrice : product?.compareAtPrice;
  const displayInStock = selectedVariant ? selectedVariant.inStock : (product?.inStock ?? true);
  const activeVariantId = selectedVariant?.id ?? product?.variantId;
  const canAdd = Boolean(product) && displayInStock && Boolean(activeVariantId);
  // Estoque da variação escolhida: limita a quantidade (a loja só vende o que tem).
  const maxQuantity = Math.min(100, variantMaxQuantity(selectedVariant) ?? 100);
  useEffect(() => { setQuantity((prev) => Math.max(1, Math.min(prev, maxQuantity))); }, [maxQuantity]);

  const handleAdd = () => {
    if (!product || !canAdd || !activeVariantId) return;
    addItem(
      {
        id: product.id,
        name: product.name,
        image: variantImage(product, selectedVariant),
        price: displayPrice,
        variantId: activeVariantId,
        variantLabel: selectedVariant ? variantLabel(selectedVariant.values) : undefined,
        maxQuantity: variantMaxQuantity(selectedVariant),
      },
      quantity
    );
    openCart();
    setToast(`${product.name} adicionado à sacola`);
    window.setTimeout(() => setToast(null), 2600);
  };

  const handleAddSuggested = (item: Product) => {
    if (!item.variantId || item.inStock === false) return;
    addItem({ id: item.id, name: item.name, image: variantImage(item, defaultVariant(item)), price: item.price, variantId: item.variantId, variantLabel: defaultVariantLabel(item), maxQuantity: variantMaxQuantity(defaultVariant(item)) });
    openCart();
    setToast(`${item.name} adicionado à sacola`);
    window.setTimeout(() => setToast(null), 2600);
  };

  if (error) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Header />
        <main className="mx-auto flex w-full max-w-none flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-primary-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-3xl font-semibold text-foreground-950">Produto não encontrado</h1>
          <p className="mt-2 text-sm text-foreground-600">{error}</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600"
          >
            Voltar para a loja
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Header />
        <main className="mx-auto flex w-full max-w-none flex-1 items-center justify-center px-4 py-24 md:px-6">
          <p role="status" className="text-sm text-foreground-600">
            Carregando produto...
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const gallery = product.images && product.images.length > 0 ? product.images : [product.image];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Header />
      <Breadcrumb current={product.name} parents={[]} />

      <main className="mx-auto w-full max-w-none flex-1 px-4 pb-16 pt-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <ProductGallery
            images={gallery}
            alt={product.name}
            activeIndex={imageIndex}
            onActiveIndexChange={setImageIndex}
          />

          <div className="flex flex-col">
            <h1 className="font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
              {product.name}
            </h1>

            <p className="mt-4 flex items-baseline gap-3">
              <span className="font-heading text-3xl font-semibold text-foreground-950">
                {formatBRL(displayPrice)}
              </span>
              {displayCompareAtPrice && displayCompareAtPrice > displayPrice && (
                <span className="text-lg text-foreground-400 line-through">
                  {formatBRL(displayCompareAtPrice)}
                </span>
              )}
            </p>

            {attributeNames.map((attrName) => (
              <div key={attrName} className="mt-6">
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-foreground-800">
                  {attrName}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(product.attributes?.[attrName] ?? []).map((value) => {
                    const active = selectedValues[attrName] === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setSelectedValues((prev) => ({ ...prev, [attrName]: value }))}
                        className={`whitespace-nowrap rounded-md border px-4 py-2.5 min-h-11 text-sm transition-colors ${
                          active
                            ? "border-primary-500 bg-primary-50 font-medium text-primary-800"
                            : "border-background-300 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center justify-between rounded-md border border-background-300 sm:justify-start">
                <button
                  type="button"
                  aria-label="Diminuir quantidade"
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  className="flex h-11 w-11 items-center justify-center text-foreground-700 transition-colors hover:text-primary-700"
                >
                  <i className="ri-subtract-line text-base"></i>
                </button>
                <span className="w-10 text-center text-sm font-medium text-foreground-900">{quantity}</span>
                <button
                  type="button"
                  aria-label="Aumentar quantidade"
                  onClick={() => setQuantity((prev) => Math.min(maxQuantity, prev + 1))}
                  className="flex h-11 w-11 items-center justify-center text-foreground-700 transition-colors hover:text-primary-700"
                >
                  <i className="ri-add-line text-base"></i>
                </button>
              </div>

              <button
                type="button"
                onClick={handleAdd}
                disabled={!canAdd}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="ri-shopping-cart-2-line text-sm"></i>
                {!displayInStock ? "Produto indisponível" : "Adicionar ao carrinho"}
              </button>
            </div>

            <p className="mt-3 text-xs text-foreground-500">
              Frete, formas de pagamento e condições finais serão apresentados no checkout.
            </p>

            {product.description && (
              <div
                className="prose prose-sm mt-8 max-w-none border-t border-background-200 pt-6 text-foreground-700"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}

            <div className="mt-7 flex flex-wrap gap-4 text-sm">
              <Link to="/politica-de-trocas" className="text-primary-700 underline hover:text-primary-800">
                Trocas e devoluções
              </Link>
              <Link to="/contato" className="text-primary-700 underline hover:text-primary-800">
                Dúvidas? Fale conosco
              </Link>
            </div>
          </div>
        </div>

        <RelatedProducts products={suggested} onAddToCart={handleAddSuggested} />
      </main>

      <FeaturesBar />
      <Footer />
      <CartDrawer />

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-40 flex w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground-950 px-4 py-3 text-sm text-background-50 shadow-lg">
          <span className="flex h-5 w-5 items-center justify-center text-primary-400">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </span>
          {toast}
        </div>
      )}
    </div>
  );
}
